import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { useGlobalData } from '../../context/GlobalDataContext';
import companiesData from '../../data/companies.json';
import { getChartColors, getChartFont } from '../../utils/chartColors';

const isValidValue = (value) => {
  return value !== undefined && value !== null;
};

const interpolateValue = (targetDate, allDates, dateMap) => {
  const target = new Date(targetDate);

  let beforeDate = null;
  let afterDate = null;
  let beforeValue = null;
  let afterValue = null;

  for (let i = 0; i < allDates.length; i++) {
    const currentDate = new Date(allDates[i]);
    const currentValue = dateMap[allDates[i]];

    if (isValidValue(currentValue)) {
      if (currentDate <= target && (!beforeDate || currentDate > new Date(beforeDate))) {
        beforeDate = allDates[i];
        beforeValue = currentValue;
      }
      if (currentDate >= target && (!afterDate || currentDate < new Date(afterDate))) {
        afterDate = allDates[i];
        afterValue = currentValue;
      }
    }
  }

  if (beforeValue !== null && afterValue !== null) {
    const beforeTime = new Date(beforeDate).getTime();
    const afterTime = new Date(afterDate).getTime();
    const targetTime = target.getTime();

    if (beforeTime === afterTime) return beforeValue;

    const ratio = (targetTime - beforeTime) / (afterTime - beforeTime);
    return beforeValue + (afterValue - beforeValue) * ratio;
  }

  if (beforeValue !== null) return beforeValue;
  if (afterValue !== null) return afterValue;

  return 0;
};

const generateDateRange = (startDate, endDate) => {
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

const AssetChart = ({ assetType }) => {
  const [themeKey, setThemeKey] = useState(0);
  const [activeDataset, setActiveDataset] = useState('trading-volume');
  const [chartData, setChartData] = useState(null);
  const [normalizeData, setNormalizeData] = useState(true);
  const [loading, setLoading] = useState(true);
  
  const colors = getChartColors();

  // Use global data context
  const { getTickerTimeSeries, loadTimeSeriesData, allTimeSeriesData, loading: globalDataLoading } = useGlobalData();

  // Listen for theme changes
  useEffect(() => {
    const checkThemeChange = () => {
      setThemeKey(prev => prev + 1);
    };
    
    // Set up observer to watch for class changes
    const observer = new MutationObserver(checkThemeChange);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }
    
    return () => observer.disconnect();
  }, []);

  // Map asset types to their tickers and company names (only tracked companies)
  const assetInfo = useMemo(() => {
    if (!companiesData || !companiesData[assetType]) {
      return { tickers: [], companyNames: [] };
    }
    const companies = (companiesData[assetType] || []).filter(c => c.trackData === true);
    return {
      tickers: companies.map(c => c.ticker),
      companyNames: companies.map(c => c.name)
    };
  }, [assetType]);

  const getCompanyColor = useCallback((index, alpha = 1) => {
    let colorArray = [];
    
    switch (assetType) {
      case 'bitcoin':
        colorArray = [
          `rgba(255, 255, 0, ${alpha})`,
          `rgba(255, 235, 59, ${alpha})`,
          `rgba(255, 193, 7, ${alpha})`,
          `rgba(255, 152, 0, ${alpha})`,
          `rgba(255, 69, 0, ${alpha})`,
          `rgba(244, 67, 54, ${alpha})`,
          `rgba(220, 20, 60, ${alpha})`,
          `rgba(255, 20, 147, ${alpha})`,
          `rgba(255, 105, 180, ${alpha})`,
          `rgba(50, 205, 50, ${alpha})`,
          `rgba(0, 255, 127, ${alpha})`,
          `rgba(0, 255, 0, ${alpha})`,
          `rgba(34, 139, 34, ${alpha})`,
          `rgba(0, 100, 0, ${alpha})`,
          `rgba(0, 206, 209, ${alpha})`,
          `rgba(64, 224, 208, ${alpha})`
        ];
        break;
      case 'ethereum':
        colorArray = [
          `rgba(173, 216, 230, ${alpha})`,
          `rgba(135, 206, 250, ${alpha})`,
          `rgba(100, 149, 237, ${alpha})`,
          `rgba(70, 130, 180, ${alpha})`,
          `rgba(65, 105, 225, ${alpha})`,
          `rgba(98, 126, 234, ${alpha})`,
          `rgba(72, 61, 139, ${alpha})`,
          `rgba(75, 0, 130, ${alpha})`,
          `rgba(138, 43, 226, ${alpha})`,
          `rgba(128, 0, 128, ${alpha})`
        ];
        break;
      case 'solana':
        colorArray = [
          `rgba(50, 205, 50, ${alpha})`,
          `rgba(46, 204, 113, ${alpha})`,
          `rgba(0, 201, 167, ${alpha})`,
          `rgba(72, 209, 204, ${alpha})`,
          `rgba(30, 144, 255, ${alpha})`,
          `rgba(70, 130, 180, ${alpha})`,
          `rgba(138, 43, 226, ${alpha})`,
          `rgba(128, 0, 128, ${alpha})`
        ];
        break;
      default:
        colorArray = [
          `rgba(98, 126, 234, ${alpha})`,
          `rgba(76, 99, 210, ${alpha})`,
          `rgba(58, 75, 153, ${alpha})`,
          `rgba(45, 58, 120, ${alpha})`,
          `rgba(34, 44, 90, ${alpha})`
        ];
    }
    
    return colorArray[index % colorArray.length];
  }, [assetType]);
  const processChartData = useCallback((allData, tickers, companyNames) => {
    // Get current theme colors (called inside function to ensure fresh values)
    const currentColors = getChartColors();
    
    // Filter out undefined/null data arrays
    const validData = allData.filter(data => data && Array.isArray(data) && data.length > 0);
    
    if (validData.length === 0) {
      return {};
    }
    
    // Get all unique dates from actual data
    const allDates = new Set();
    validData.forEach(data => {
      data.forEach(row => allDates.add(row.date));
    });
    const dataOnlyDates = Array.from(allDates).sort();
    
    // Generate complete date range including weekends
    if (dataOnlyDates.length === 0) {
      return {};
    }
    let sortedDates = generateDateRange(dataOnlyDates[0], dataOnlyDates[dataOnlyDates.length - 1]);

    if (assetType === 'bitcoin') {
      const bitcoinStartDate = '2025-06-15';
      sortedDates = sortedDates.filter(date => date >= bitcoinStartDate);
    }
    
    // For Solana asset, filter to start from May 5, 2025
    if (assetType === 'solana') {
      const solanaStartDate = '2025-05-05';
      sortedDates = sortedDates.filter(date => date >= solanaStartDate);
    }

    const createChart = (dataKey, title, normalize = true) => {
      // Pre-calculate date maps for ALL companies upfront (batched)
      const allCompanyDateMaps = validData.map((companyData, idx) => {
        const companyDateMap = {};
        companyData.forEach(row => {
          // Normalize date format
          const normalizedDate = row.date instanceof Date 
            ? row.date.toISOString().split('T')[0]
            : row.date.split('T')[0];
          
          // For crypto holdings, calculate from crypto_per_share * outstanding_shares_estimated
          if (dataKey === 'crypto_holdings_usd') {
            const cryptoHoldings = (row.crypto_per_share || 0) * (row.outstanding_shares_estimated || 0);
            companyDateMap[normalizedDate] = cryptoHoldings;
          } else {
            companyDateMap[normalizedDate] = row[dataKey];
          }
        });
        return companyDateMap;
      });
      
      // Find first valid date for each company (before calculating totals)
      const companyFirstValidDates = allCompanyDateMaps.map((companyDateMap, idx) => {
        for (let i = 0; i < sortedDates.length; i++) {
          const date = sortedDates[i];
          const value = companyDateMap[date];
          if (isValidValue(value) && value !== 0) {
            return date;
          }
        }
        return null;
      });
      
      // Pre-calculate totals for all dates (once, not per ticker)
      // Use same logic as individual values: 0 before first valid date, interpolate after
      const totals = sortedDates.map(date => {
        return allCompanyDateMaps.reduce((sum, companyDateMap, idx) => {
          const rawValue = companyDateMap[date];
          if (isValidValue(rawValue)) {
            return sum + rawValue;
          }
          
          // If date is before the first valid date for this company, use 0
          const firstValidDate = companyFirstValidDates[idx];
          if (firstValidDate && new Date(date) < new Date(firstValidDate)) {
            return sum + 0;
          }
          
          // For dates after first valid date, use interpolation
          return sum + interpolateValue(date, sortedDates, companyDateMap);
        }, 0);
      });
      
      // Now process each company using pre-calculated data
      const traces = validData.map((companyData, index) => {
        const ticker = tickers[index];
        const dateMap = allCompanyDateMaps[index];
        
        // Find the first date with a valid (non-zero) value for this company
        let firstValidDate = null;
        for (let i = 0; i < sortedDates.length; i++) {
          const date = sortedDates[i];
          const value = dateMap[date];
          if (isValidValue(value) && value !== 0) {
            firstValidDate = date;
            break;
          }
        }
        
        // Get values for all dates, with interpolation for missing/invalid dates
        const values = sortedDates.map(date => {
          const value = dateMap[date];
          if (isValidValue(value)) {
            return value;
          }
          
          // If date is before the first valid date, return 0
          if (firstValidDate && new Date(date) < new Date(firstValidDate)) {
            return 0;
          }
          
          // For dates after the first valid date, use linear interpolation
          return interpolateValue(date, sortedDates, dateMap);
        });

        // Calculate percentages using pre-calculated totals if normalize is true
        const displayValues = normalize 
          ? values.map((value, idx) => {
              const total = totals[idx];
              return total > 0 ? (value / total) * 100 : 0;
            })
          : values;

        // Calculate average value for sorting (use latest value or average)
        const avgValue = displayValues.length > 0 
          ? displayValues.reduce((sum, val) => sum + val, 0) / displayValues.length
          : 0;

        return {
          x: sortedDates,
          y: displayValues,
          type: 'scatter',
          mode: 'lines',
          fill: normalize ? 'tonexty' : undefined,
          stackgroup: normalize ? 'one' : undefined,
          name: ticker,
          line: { 
            color: getCompanyColor(index),
            dash: 'solid'
          },
          fillcolor: getCompanyColor(index, 0.3),
          _avgValue: avgValue // Store for sorting
        };
      });
      
      // Sort traces by average value (ascending) so smallest values are at the bottom of stacked chart
      traces.sort((a, b) => {
        return (a._avgValue || 0) - (b._avgValue || 0);
      });
      
      return {
        title: title,
        data: traces,
        layout: {
          xaxis: { 
            title: { 
              text: 'Date', 
              font: getChartFont(12)
            }, 
            color: currentColors.text, 
            gridcolor: currentColors.grid 
          },
          yaxis: { 
            title: { 
              text: normalize ? 'Market Share (%)' : 'Value', 
              font: getChartFont(12)
            }, 
            color: currentColors.text, 
            gridcolor: currentColors.grid,
            type: normalize ? 'linear' : 'log'
          },
          plot_bgcolor: currentColors.background,
          paper_bgcolor: currentColors.background,
          font: getChartFont(12),
          margin: { l: 80, r: 30, t: 30, b: 60 },
          showlegend: true,
          legend: {
            font: getChartFont(12),
            traceorder: 'normal' // Use the order of traces in the data array
          }
        }
      };
    };

    // Create both normalized and raw versions
    const tradingVolumeChartNormalized = createChart('turnover', 'Trading Volume by Company (%)', true);
    const marketCapChartNormalized = createChart('mcap_estimated', 'Market Cap by Company (%)', true);
    const cryptoHoldingsChartNormalized = createChart('crypto_holdings_usd', 'Crypto Holdings by Company (%)', true);
    
    const tradingVolumeChartRaw = createChart('turnover', 'Trading Volume by Company', false);
    const marketCapChartRaw = createChart('mcap_estimated', 'Market Cap by Company', false);
    const cryptoHoldingsChartRaw = createChart('crypto_holdings_usd', 'Crypto Holdings by Company', false);
    
    return {
      normalized: {
        'trading-volume': tradingVolumeChartNormalized,
        'market-cap': marketCapChartNormalized,
        'crypto-holdings': cryptoHoldingsChartNormalized
      },
      raw: {
        'trading-volume': tradingVolumeChartRaw,
        'market-cap': marketCapChartRaw,
        'crypto-holdings': cryptoHoldingsChartRaw
      }
    };
  }, [assetType, getCompanyColor]);

  // Fetch real data for all companies in this asset
  useEffect(() => {
    const fetchChartData = async () => {
      if (globalDataLoading) {
        return;
      }

      try {
        setLoading(true);
        const { tickers, companyNames } = assetInfo;

        const assetName = assetType === 'bitcoin' ? 'bitcoin' :
                         assetType === 'ethereum' ? 'ethereum' :
                         assetType === 'solana' ? 'solana' : null;

        const hasAssetData = tickers.some(ticker => {
          const tickerData = getTickerTimeSeries(ticker);
          return tickerData && tickerData.length > 0;
        });

        if (assetName && !hasAssetData) {
          await loadTimeSeriesData(assetName);
        }

        const allData = tickers.map(ticker => getTickerTimeSeries(ticker));
        const processedData = processChartData(allData, tickers, companyNames);
        setChartData(processedData);
      } catch (error) {
        // swallow error; chart will show fallback
      } finally {
        setLoading(false);
      }
    };

    if (assetType) {
      fetchChartData();
    }
  }, [assetType, globalDataLoading, loadTimeSeriesData, getTickerTimeSeries, assetInfo, processChartData]);

  const getAssetColor = () => {
    switch (assetType) {
      case 'bitcoin': return '#f7931a';
      case 'ethereum': return '#627eea';
      case 'solana': return '#9945ff';
      default: return '#627eea';
    }
  };

  const assetColor = getAssetColor();

  if (loading || globalDataLoading || !allTimeSeriesData) {
    return (
      <div className="asset-chart-container" style={{ '--crypto-color': assetColor }}>
        <div className="chart-header">
          <h3 className="chart-title">Asset Analytics</h3>
          <div className="chart-controls">
            <button className="chart-button active">Trading Volume ($)</button>
            <button className="chart-button">Market Cap ($)</button>
            <button className="chart-button">Crypto Holdings</button>
          </div>
        </div>
        <div className="chart-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px', color: colors.text }}>
            Loading chart data...
          </div>
        </div>
      </div>
    );
  }

  if (!chartData || !chartData.normalized || !chartData.raw) {
    return (
      <div className="asset-chart-container" style={{ '--crypto-color': assetColor }}>
        <div className="chart-header">
          <h3 className="chart-title">Asset Analytics</h3>
        </div>
        <div className="chart-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px', color: colors.text }}>
            No data available
          </div>
        </div>
      </div>
    );
  }

  const dataSet = normalizeData ? chartData.normalized : chartData.raw;
  const currentData = dataSet && dataSet[activeDataset] ? dataSet[activeDataset] : null;

  if (!currentData || !currentData.data) {
    return (
      <div className="asset-chart-container" style={{ '--crypto-color': assetColor }}>
        <div className="chart-header">
          <h3 className="chart-title">Asset Analytics</h3>
          <div className="chart-controls">
            <button 
              className={`chart-button ${activeDataset === 'trading-volume' ? 'active' : ''}`}
              onClick={() => setActiveDataset('trading-volume')}
            >
              Trading Volume ($)
            </button>
            <button 
              className={`chart-button ${activeDataset === 'market-cap' ? 'active' : ''}`}
              onClick={() => setActiveDataset('market-cap')}
            >
              Market Cap ($)
            </button>
            <button 
              className={`chart-button ${activeDataset === 'crypto-holdings' ? 'active' : ''}`}
              onClick={() => setActiveDataset('crypto-holdings')}
            >
              Crypto Holdings
            </button>
          </div>
        </div>
        <div className="chart-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px', color: colors.text }}>
            No data available for {activeDataset}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="asset-chart-container" style={{ '--crypto-color': assetColor }}>
      <div className="chart-header">
        <h3 className="chart-title">Asset Analytics</h3>
        <div className="chart-controls">
          <button 
            className={`chart-button ${activeDataset === 'trading-volume' ? 'active' : ''}`}
            onClick={() => setActiveDataset('trading-volume')}
          >
            Trading Volume ($)
          </button>
          <button 
            className={`chart-button ${activeDataset === 'market-cap' ? 'active' : ''}`}
            onClick={() => setActiveDataset('market-cap')}
          >
            Market Cap ($)
          </button>
          <button 
            className={`chart-button ${activeDataset === 'crypto-holdings' ? 'active' : ''}`}
            onClick={() => setActiveDataset('crypto-holdings')}
          >
            Crypto Holdings
          </button>
        </div>
      </div>
      
      <div className="chart-content" style={{ position: 'relative' }}>
        <Plot
          key={themeKey}
          data={currentData.data}
          layout={currentData.layout}
          style={{ width: '100%', height: '500px' }}
          config={{ 
            displayModeBar: false,
            responsive: true
          }}
        />
        <div style={{ 
          position: 'absolute', 
          bottom: '-15px', 
          right: '10px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          zIndex: 1000,
          background: document.querySelector('.landing-page')?.classList.contains('light-mode') 
            ? 'rgba(255, 255, 255, 0.9)' 
            : 'rgba(15, 15, 15, 0.9)',
          color: colors.text,
          padding: '8px 12px',
          borderRadius: '6px',
          border: `1px solid ${colors.legendBorder}`
        }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            color: colors.text,
            userSelect: 'none'
          }}>
            <input 
              type="checkbox" 
              checked={normalizeData}
              onChange={(e) => setNormalizeData(e.target.checked)}
              style={{ 
                cursor: 'pointer',
                width: '16px',
                height: '16px'
              }}
            />
            Normalize data
          </label>
        </div>
      </div>
    </div>
  );
};

export default AssetChart;
